import { Text } from "@overmap-ai/blocks"
import { type FormSubmission, type Stored, UNLABELLED_ASSET_LABEL, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { selectAssetById, selectFormById, selectIssueById, selectUserById } from "@overmap-ai/core"
import { gray } from "@radix-ui/colors"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { ProjectTimelineItemType } from "../../enums/projects"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { LuIconWithWrapper } from "../Icons/LuIconWithWrapper"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { FormSubmissionIndexLabel } from "../IndexLabels/FormSubmissionIndexLabel"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { ProjectTimelineCard } from "./ProjectTimelineCard"

interface ProjectTimelineFormSubmissionItemProps {
	formSubmission: Stored<FormSubmission>
}

export const ProjectTimelineFormSubmissionItem = memo<ProjectTimelineFormSubmissionItemProps>((props) => {
	const { formSubmission } = props

	const createdBy = useAppSelector(selectUserById(formSubmission.created_by ?? -1))
	const form = useAppSelector(selectFormById(formSubmission.form))!
	const asset = useAppSelector(selectAssetById(formSubmission.asset ?? ""))
	const issue = useAppSelector(selectIssueById(formSubmission.issue ?? ""))

	return (
		<ProjectTimelineCard type={ProjectTimelineItemType.FORM_SUBMISSION}>
			<Text size="sm" accentColor="base" className="min-w-0">
				<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
				&nbsp;created form submission in project
				<Text accentColor="base">&nbsp;&#8226;&nbsp;{formatCompactDate(formSubmission.submitted_at)}</Text>
			</Text>

			<Text accentColor="base" className="min-w-0 truncate" size="sm">
				<LuIconWithWrapper icon={modelIconMapping.form} color={gray.gray9} background className="align-sub" />
				&nbsp;
				<Link
					className={interactiveTextCva()}
					from="/$organizationId/$projectId"
					to="/$organizationId/$projectId/forms/submissions/$formSubmissionId"
					params={{
						formSubmissionId: formSubmission.uuid,
					}}
				>
					<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} highContrast />
					&nbsp;
					<Text accentColor="base">{form.name}</Text>
				</Link>
			</Text>

			{asset || issue ? (
				<Text size="sm" accentColor="base" className="min-w-0 truncate">
					Linked to&nbsp;
					{asset ? (
						<Link
							className={interactiveTextCva()}
							from="/$organizationId/$projectId"
							to="/$organizationId/$projectId/assets/$assetId"
							params={{ assetId: asset.uuid }}
						>
							<AssetIndexLabel assetId={asset.uuid} highContrast />
							&nbsp;
							<Text accentColor="base">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
						</Link>
					) : null}
					{asset && issue ? <Text accentColor="base">&nbsp;and&nbsp;</Text> : null}
					{issue ? (
						<Link
							className={interactiveTextCva()}
							from="/$organizationId/$projectId"
							to="/$organizationId/$projectId/issues/$issueId"
							params={{ issueId: issue.uuid }}
						>
							<IssueIndexLabel issueId={issue.uuid} highContrast />
							&nbsp;
							<Text accentColor="base">{issue.title || UNTITLED_ISSUE_TITLE}</Text>
						</Link>
					) : null}
				</Text>
			) : null}
		</ProjectTimelineCard>
	)
})
ProjectTimelineFormSubmissionItem.displayName = "ProjectTimelineFormSubmissionItem"
